import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Plus, Trash2, Save, Loader2, Star, Languages, X } from "lucide-react";
import api from "../../lib/adminApi";
import { toast } from "sonner";

const LANGS = [
  { code: "tr", label: "Türkçe" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "ar", label: "العربية" },
];

const EMPTY_REVIEW = {
  author: "",
  rating: 5,
  text: { tr: "", en: "", es: "", de: "", fr: "", ar: "" },
  source: "Google",
  date: new Date().toISOString().slice(0, 10),
  tags: [],
  featured: false,
};

function ReviewEditModal({ review: initial, onSave, onClose }) {
  const [rev, setRev] = useState(() => ({
    ...EMPTY_REVIEW,
    ...initial,
    text: typeof initial?.text === "string"
      ? { tr: initial.text, en: "", es: "", de: "", fr: "", ar: "" }
      : { tr: "", en: "", es: "", de: "", fr: "", ar: "", ...(initial?.text || {}) },
  }));
  const [activeLang, setActiveLang] = useState("tr");
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  const setText = (lang, val) =>
    setRev((r) => ({ ...r, text: { ...r.text, [lang]: val } }));

  const handleSave = async () => {
    if (!rev.author.trim()) { toast.error("Müşteri adı gerekli"); return; }
    if (!rev.text.tr?.trim()) { toast.error("Türkçe yorum gerekli"); return; }
    setSaving(true);
    try {
      if (rev.id) {
        await api.put(`/admin/reviews/${rev.id}`, rev);
      } else {
        await api.post("/admin/reviews", rev);
      }
      toast.success("Yorum kaydedildi");
      onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const translateAll = async () => {
    if (!rev.text.tr?.trim()) { toast.error("Önce TR metnini yaz"); return; }
    setTranslating(true);
    const targets = ["en", "es", "de", "fr", "ar"];
    let done = 0;
    for (const lang of targets) {
      try {
        const r = await api.post("/translate", {
          text: rev.text.tr,
          source_lang: "tr",
          target_langs: [lang],
        });
        const translated = r.data?.translations?.[lang];
        if (translated) {
          setText(lang, translated);
          done++;
        }
      } catch (e) {
        const msg = e?.response?.data?.detail;
        const str = typeof msg === "string" ? msg : JSON.stringify(msg);
        if (str?.includes("budget") || str?.includes("limit")) {
          toast.error(`Limit aşıldı — ${done}/${targets.length} dil çevrildi`);
          break;
        }
      }
      await new Promise((r) => setTimeout(r, 500));
    }
    if (done === targets.length) toast.success("Tüm diller çevrildi!");
    setTranslating(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl border border-mist shadow-2xl w-full max-w-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-mist">
          <h2 className="font-display text-h2 font-bold text-ink">
            {rev.id ? "Yorumu Düzenle" : "Yeni Yorum Ekle"}
          </h2>
          <button type="button" onClick={onClose}
            className="h-8 w-8 rounded-full bg-mist flex items-center justify-center hover:bg-mist/80">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Basic fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-base">Müşteri Adı *</label>
              <input className="input-base" value={rev.author}
                onChange={(e) => setRev((r) => ({ ...r, author: e.target.value }))}
                placeholder="Ahmet Y." />
            </div>
            <div>
              <label className="label-base">Yıldız (1-5)</label>
              <div className="flex items-center gap-1 mt-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setRev((r) => ({ ...r, rating: n }))}>
                    <Star className={`h-6 w-6 transition-colors ${n <= rev.rating ? "fill-gold text-gold" : "text-mist"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-base">Kaynak</label>
              <select className="input-base" value={rev.source}
                onChange={(e) => setRev((r) => ({ ...r, source: e.target.value }))}>
                <option>Google</option>
                <option>LinkedIn</option>
                <option>Direct</option>
              </select>
            </div>
            <div>
              <label className="label-base">Tarih</label>
              <input type="date" className="input-base" value={rev.date}
                onChange={(e) => setRev((r) => ({ ...r, date: e.target.value }))} />
            </div>
          </div>

          {/* Language text editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-base mb-0">Yorum Metni</label>
              <button type="button" onClick={translateAll} disabled={translating}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-pink/40 px-3 py-1 text-xs font-semibold text-brand-pink hover:bg-brand-pink hover:text-cream transition-colors disabled:opacity-60">
                {translating
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Çevriliyor…</>
                  : <><Languages className="h-3.5 w-3.5" /> TR'den 5 dile çevir</>}
              </button>
            </div>

            {/* Lang tabs */}
            <div className="flex gap-1 flex-wrap mb-2">
              {LANGS.map((l) => (
                <button key={l.code} type="button"
                  onClick={() => setActiveLang(l.code)}
                  className={`rounded-full px-3 py-1 text-xs font-bold uppercase transition-colors ${
                    activeLang === l.code ? "bg-ink text-cream" : "bg-mist text-ink/70 hover:bg-mist/70"
                  }`}
                >
                  {l.code.toUpperCase()}
                  {rev.text[l.code]?.trim() ? (
                    <span className="ml-1 h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block align-middle" />
                  ) : null}
                </button>
              ))}
            </div>

            <textarea
              rows={4}
              className="input-base resize-y"
              placeholder={activeLang === "tr" ? "Yorum metni yazın…" : `${activeLang.toUpperCase()} için yorum (TR'den çeviri butonu ile otomatik çevrilebilir)`}
              value={rev.text[activeLang] || ""}
              onChange={(e) => setText(activeLang, e.target.value)}
            />
            {activeLang !== "tr" && !rev.text[activeLang]?.trim() && (
              <p className="text-xs text-amber-600 mt-1">⚠️ Bu dil için metin yok — sitede TR metni gösterilir.</p>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="label-base">Etiketler (hizmet slug'ları, virgülle)</label>
            <input className="input-base"
              value={Array.isArray(rev.tags) ? rev.tags.join(", ") : ""}
              onChange={(e) => setRev((r) => ({
                ...r, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
              }))}
              placeholder="meta-ads, seo, creative" />
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!rev.featured}
              onChange={(e) => setRev((r) => ({ ...r, featured: e.target.checked }))} />
            Öne çıkan yorum (Ana sayfa öncelikli)
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-mist">
          <button type="button" onClick={onClose}
            className="btn-outline-ink !text-sm">İptal</button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="btn-gold !text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reviews");
      setReviews(r.data?.reviews || r.data || []);
    } catch { setReviews([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteReview = async (id) => {
    if (!window.confirm("Bu yorumu silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Yorum silindi");
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const getLangCoverage = (text) => {
    if (typeof text !== "object") return 0;
    return LANGS.filter((l) => text[l.code]?.trim()).length;
  };

  return (
    <div data-testid="admin-reviews-page">
      <Helmet><title>Yorumlar · Yastık Medya Admin</title></Helmet>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Müşteri Yorumları</h1>
          <p className="text-sm text-ink/55 mt-1">
            Her yorum 6 dilde düzenlenebilir. Dolu dil sayısı yeşil nokta ile gösterilir.
          </p>
        </div>
        <button type="button"
          onClick={() => { setEditingReview({ ...EMPTY_REVIEW }); setShowModal(true); }}
          className="btn-gold inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Yeni Yorum
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-brand-pink" /></div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => {
            const langCount = getLangCoverage(r.text);
            return (
              <div key={r.id} className="rounded-2xl bg-white border border-mist p-5 flex items-start gap-4">
                {/* Stars */}
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < (r.rating || 5) ? "fill-gold text-gold" : "text-mist"}`} />
                  ))}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-ink">{r.author}</span>
                    <span className="text-xs text-ink/40">{r.date}</span>
                    <span className="rounded-full bg-mist px-2 py-0.5 text-[0.6rem] font-bold uppercase text-ink/50">{r.source}</span>
                    {/* Lang coverage indicator */}
                    <div className="flex items-center gap-0.5">
                      {LANGS.map((l) => {
                        const text = typeof r.text === "object" ? r.text[l.code] : (l.code === "tr" ? r.text : "");
                        return (
                          <span key={l.code} title={`${l.code.toUpperCase()}: ${text ? "✓" : "✗"}`}
                            className={`h-2 w-2 rounded-full ${text?.trim() ? "bg-emerald-400" : "bg-mist"}`} />
                        );
                      })}
                      <span className="text-[0.6rem] text-ink/40 ml-1">{langCount}/6 dil</span>
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-ink/70 line-clamp-2">
                    {typeof r.text === "object" ? r.text.tr : r.text}
                  </p>
                  {r.tags?.length > 0 && (
                    <div className="mt-1.5 flex gap-1 flex-wrap">
                      {r.tags.map((t) => (
                        <span key={t} className="rounded-full bg-brand-pink/10 text-brand-pink text-[0.6rem] px-2 py-0.5 font-medium">{t}</span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  <button type="button"
                    onClick={() => { setEditingReview(r); setShowModal(true); }}
                    className="rounded-lg border border-mist px-3 py-1.5 text-xs font-semibold text-ink hover:border-brand-pink hover:text-brand-pink transition-colors">
                    Düzenle
                  </button>
                  <button type="button" onClick={() => deleteReview(r.id)}
                    className="rounded-lg border border-destructive/30 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/5 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && editingReview && (
        <ReviewEditModal
          review={editingReview}
          onSave={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
